import React from 'react';
import {
  Box,
  Card,
  CardContent,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
} from '@mui/icons-material';

interface HorizontalSlide {
  id: string;
  column: number;
  page?: any;
  isEmpty: boolean;
}

interface SlideCardProps {
  slide: HorizontalSlide;
  isUploading: boolean;
  draggedSlide: HorizontalSlide | null;
  onDragStart: (e: React.DragEvent, slide: HorizontalSlide) => void;
  onDrop: (e: React.DragEvent, slide: HorizontalSlide) => void;
  onDragEnd: () => void;
  onImageSelect: (slideId: string, event: React.MouseEvent<HTMLElement>) => void;
  onDeleteSlide: (slide: HorizontalSlide) => void;
  onProcessImageFile: (file: File, slideId: string) => void;
}

export const SlideCard: React.FC<SlideCardProps> = React.memo(({
  slide,
  isUploading,
  draggedSlide,
  onDragStart,
  onDrop,
  onDragEnd,
  onImageSelect,
  onDeleteSlide,
  onProcessImageFile,
}) => {
  return (
    <Card 
      sx={{ 
        width: 168,
        height: 168, // 1:1の比率
        cursor: slide.page ? 'grab' : 'pointer',
        border: '1px solid #e0e0e0',
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 1,
        boxShadow: 'none',
        transition: 'opacity 0.2s',
        opacity: draggedSlide?.id === slide.id ? 0.5 : isUploading ? 0.7 : 1,
        '&:active': {
          cursor: slide.page ? 'grabbing' : 'pointer'
        }
      }}
      onClick={(e) => {
        // スライド全体をクリックで画像アップロードを開く
        onImageSelect(slide.id, e);
      }}
      draggable={!!slide.page}
      onDragStart={(e) => onDragStart(e, slide)}
      onDragOver={(e) => {
        e.preventDefault();
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
          e.dataTransfer.dropEffect = 'copy';
        } else {
          e.dataTransfer.dropEffect = 'move';
        }
      }}
      onDrop={(e) => onDrop(e, slide)}
      onDragEnd={onDragEnd}
    >
      <CardContent sx={{ p: 0, height: '100%', position: 'relative', '&:last-child': { pb: 0 } }}>
        {slide.page ? (
          <>
            {/* 背景画像 */}
            {slide.page.backgroundImage && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundImage: `url(${slide.page.backgroundImage})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
            )}
            
            {/* ドラッグハンドル（サムネイル下部にグレー背景の帯として表示） */}
            <Box 
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                backgroundColor: 'rgba(128, 128, 128, 0.5)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 0.5,
                zIndex: 2,
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gridTemplateRows: 'repeat(2, 1fr)',
                  gap: 0.5,
                }}
              >
                {[...Array(6)].map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      backgroundColor: 'white',
                    }}
                  />
                ))}
              </Box>
            </Box>

            {/* hover時のオーバーレイ（霧がかかった感じ） */}
            <Box 
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(128, 128, 128, 0.5)',
                backdropFilter: 'blur(4px)',
                WebkitBackdropFilter: 'blur(4px)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                opacity: 0,
                transition: 'opacity 0.2s',
                zIndex: 1,
                '.MuiCard-root:hover &': {
                  opacity: 1
                }
              }}
            >
              {/* 中央の画像アイコン */}
              <ImageIcon sx={{ fontSize: '2rem', color: 'text.secondary' }} />
            </Box>

            {/* hover時のアクションボタン（帯の上に表示、霧より上） */}
            <Box
              sx={{
                position: 'absolute',
                bottom: '24px',
                left: 0,
                right: 0,
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                px: 1,
                py: 0.75,
                opacity: 0,
                transition: 'opacity 0.2s',
                zIndex: 2,
                '.MuiCard-root:hover &': {
                  opacity: 1
                }
              }}
            >
              {/* 削除アイコン */}
              <IconButton 
                size="small" 
                sx={{ 
                  backgroundColor: 'rgba(128, 128, 128, 0.6)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  color: 'white',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: 'rgba(128, 128, 128, 0.7)',
                  },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteSlide(slide);
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Box>

            {/* アップロード中インジケーター */}
            {isUploading && (
              <Box 
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  borderRadius: '50%',
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                }}
              >
                <CircularProgress size={20} sx={{ color: 'white' }} />
              </Box>
            )}
          </>
        ) : (
          // 空のスライド
          <Box
            sx={{
              height: '100%',
              position: 'relative',
              backgroundColor: '#f9f9f9',
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = 'copy';
            }}
            onDrop={(e) => {
              e.preventDefault();
              const files = Array.from(e.dataTransfer.files);
              const imageFile = files.find(file => file.type.startsWith('image/'));
              
              if (imageFile) {
                onProcessImageFile(imageFile, slide.id);
              }
            }}
          >
            {/* 中央のプラスアイコン（通常時のみ表示） */}
            <Box 
              sx={{ 
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                opacity: 1,
                transition: 'opacity 0.2s',
                '.MuiCard-root:hover &': {
                  opacity: 0
                }
              }}
            >
              <AddIcon sx={{ fontSize: '3rem', color: 'text.secondary' }} />
            </Box>

            {/* hover時のオーバーレイ（霧がかかった感じ） */}
            <Box 
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(128, 128, 128, 0.5)',
                backdropFilter: 'blur(4px)',
                WebkitBackdropFilter: 'blur(4px)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                opacity: 0,
                transition: 'opacity 0.2s',
                '.MuiCard-root:hover &': {
                  opacity: 1
                }
              }}
            >
              {/* 中央の画像アイコン */}
              <ImageIcon sx={{ fontSize: '2rem', color: 'text.secondary' }} />
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
});

SlideCard.displayName = 'SlideCard';
